import { translate as t } from '@thoughtspot/i18n';
import { generateUUID } from '@thoughtspot/js-util';
import _ from 'lodash';
import {
    FalconDataType,
    ParameterOwnerType,
    ParameterValueListItem,
} from '/@services/generated/graphql-types';
import { getDateStringFromEpoch } from '/@utils/date-time.util';
import {
    getDateFromEpoch,
    getEpochFromDate,
} from '../filters/date-picker/date-filter.util';

export const GREGORIAN_CALENDAR_ID = '7573c08b-753b-478b-84fd-6e702d481ff6';
export const FISCAL_CALENDAR_ID = 'bfa39848-ba4f-46d8-80fd-b695064e61b7';

export enum ParameterUISurface {
    Answer = 'answer',
    Model = 'model',
    Liveboard = 'liveboard',
}

export const parameterDataTypes = [
    FalconDataType.Int64,
    FalconDataType.Double,
    FalconDataType.Char,
    FalconDataType.Bool,
    FalconDataType.Date,
];

export const getCurrentParameterOwner = (ownerType: ParameterOwnerType) => {
    switch (ownerType) {
        case ParameterOwnerType.Answer:
            return t('dependencyModal.currentAnswer');
        case ParameterOwnerType.Worksheet:
            return t('dependencyModal.currentWorksheet');
        default:
            return null;
    }
};

// This is only used for Mixpanel metrics, so no need for i18n
export enum ParameterInputFields {
    NAME = 'Name',
    DEFAULT = 'Default value',
    RANGE = 'Range value',
    LIST = 'List value',
    BOOL = 'Bool value',
    OVERRIDE = 'Override value',
}

// This is only used for Mixpanel metrics, so no need for i18n
export enum SaveError {
    EMPTY = 'empty',
    DUPLICATE = 'duplicate',
    INVALID = 'invalid',
}

export enum SegmentControlState {
    INTEGER,
    DOUBLE,
    STRING,
    BOOLEAN,
    DATE,
}

export enum DateDefaultType {
    DYNAMIC = 'DYNAMIC',
    FIXED = 'FIXED',
}

export const getEmptyRowObj = () => ({
    id: generateUUID(),
    value: '',
    displayAs: '',
});

export const getIdxDataTypeMap = (dataTypes = parameterDataTypes) => {
    const dataTypeToIdx: Partial<Record<FalconDataType, number>> = {};
    for (let i = 0; i < dataTypes.length; i++) {
        dataTypeToIdx[dataTypes[i]] = i;
    }
    return dataTypeToIdx;
};

export const getValueListWithGeneratedIdsAndConvertedDates = (
    valueList: ParameterValueListItem[],
    isDateType: boolean,
) => {
    return valueList
        ? valueList.map(list => ({
              value: isDateType
                  ? getDateStringFromEpoch(list.value)
                  : list.value,
              displayAs: list.displayAs,
              id: generateUUID(),
          }))
        : [getEmptyRowObj()];
};

// check if a string int/double input is valid
export const isValidNumberType = (
    dataType: FalconDataType.Int64 | FalconDataType.Double,
    value: string,
) => {
    const numberData = _.toNumber(value);

    if (Number.isNaN(numberData)) {
        return false;
    }
    if (dataType === FalconDataType.Int64) {
        if (_.isInteger(numberData)) {
            return true;
        }
        return false;
    }
    return true;
};

export const getInputValueFor = (value: string, dataType: FalconDataType) => {
    if (!value) return '';

    return dataType !== FalconDataType.Date
        ? value ?? ''
        : getDateFromEpoch(value);
};

export const getTrimmedValueFrom = (value: string | Date) => {
    let trimmedValue = null;
    if (_.isString(value)) {
        trimmedValue = value.trim();
    } else {
        trimmedValue = (getEpochFromDate(value) ?? '').toString();
    }
    return trimmedValue;
};

export const isParameterValueEqual = (
    valueA: string | null,
    valueB: string | null,
    dataType: FalconDataType,
): boolean => {
    // For numbers, we must convert it to a number first, to handle situations like "4" vs "4.0"
    if (
        dataType === FalconDataType.Int64 ||
        dataType === FalconDataType.Double
    ) {
        // _.toNumber(null) is 0, we want to avoid issues around that
        if (!_.isNil(valueA) && !_.isNil(valueB)) {
            return _.toNumber(valueA) === _.toNumber(valueB);
        }
    }

    // All other data types should be comparable directly in string form
    return valueA === valueB;
};
